// Block 3: „Audit-Empfehlungen actionable" — Aktions-Leiste + Fix-Vorschau
// pro strukturellem Befund (Flaw) im Audit-Report.
//
// Drei Einstiege pro Flaw-Karte:
// - „Einbauen": propose-fix → Vorschau-Karte (exakte Änderungsliste,
//   dropped_actions, Staleness-Hinweis) mit Übernehmen/Verwerfen.
// - „Mit Modifikationen einbauen": dieselbe Vorschau, aber Textfelder und
//   Link-Auswahl editierbar — Übernehmen wendet die bearbeitete Fassung an.
// - „Mit der KI besprechen": öffnet den DidaktikChat im Kurs-Kontext,
//   vorbefüllt mit Befund + Empfehlung (macht die Parent-Route).
//
// „Übernehmen" führt die Aktionen sequentiell über die bestehenden
// PATCH/POST-Endpunkte aus, sammelt Fehler statt still abzubrechen und lässt
// den Parent den Kurs neu laden. Es wird nie etwas gelöscht.

import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  api,
  APIError,
  isQuotaError,
  type FixAction,
  type ProposeFixResponse,
} from "@/api/client";
import CreditCost from "@/components/CreditCost";
import QuotaExhaustedCard from "@/components/QuotaExhaustedCard";

// Bender-Tiefenstufen sind Domain-Werte aus dem Backend (deutsch, DB-Enum) —
// bewusst NICHT übersetzt (Pendant: lib/depth.ts, AuditReport.tsx).
const BENDER_DEPTHS = ["Wissen", "Verstehen", "Können", "Performanz"] as const;

const UPDATE_FIELDS: Record<string, string[]> = {
  update_ilo: ["text", "action_verb", "bender_depth"],
  update_tla: ["description", "type", "modality", "duration_minutes"],
  update_at: ["description", "type", "format", "duration_minutes"],
};

interface FlawFixProps {
  courseId: string;
  reportId: string;
  flawIndex: number;
  // UUIDs der Lernziele in Kurs-Reihenfolge (Position = Nummer) — Grundlage
  // für die LZ-Chips im Edit-Modus.
  iloIds: string[];
  onApplied: () => void;
  onDiscuss: () => void;
}

type Phase = "idle" | "loading" | "preview" | "edit" | "applying" | "done";

export default function FlawFix(props: FlawFixProps) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>("idle");
  const [fix, setFix] = useState<ProposeFixResponse | null>(null);
  // Arbeitskopie der Aktionen für den Edit-Modus.
  const [editActions, setEditActions] = useState<FixAction[]>([]);
  const [applyErrors, setApplyErrors] = useState<string[]>([]);
  const [proposeError, setProposeError] = useState<string | null>(null);
  const [quota, setQuota] = useState<unknown>(null);

  // Menschenlesbare Zeile pro Aktion („Lernziel 2: Text neu",
  // „Prüfung 1 → verknüpft mit LZ 2").
  function actionLine(a: FixAction): string {
    const n = a.ilo_number ?? a.tla_number ?? a.at_number ?? 0;
    if (a.action.startsWith("update_")) {
      const fields = (UPDATE_FIELDS[a.action] || [])
        .filter((f) => a[f as keyof FixAction] != null)
        .map((f) => t(`flawfix.field.${f}`))
        .join(", ");
      return t(`flawfix.line.${a.action}`, { n, fields });
    }
    if (a.action.startsWith("link_")) {
      const numbers = a.linked_ilo_numbers ?? [];
      const links = numbers.length
        ? numbers.map((k) => `LZ ${k}`).join(", ")
        : t("flawfix.linksNone");
      return t(`flawfix.line.${a.action}`, { n, links });
    }
    return t(`flawfix.line.${a.action}`, { type: a.type ?? "?" });
  }

  async function propose(mode: "preview" | "edit") {
    setPhase("loading");
    setProposeError(null);
    setApplyErrors([]);
    try {
      const res = await api.proposeFix(props.courseId, props.reportId, props.flawIndex);
      setFix(res);
      // Tiefe Arbeitskopie für den Edit-Modus (Listen nicht teilen).
      setEditActions(
        res.actions.map((a) => ({
          ...a,
          linked_ilo_ids: [...(a.linked_ilo_ids ?? [])],
          linked_ilo_numbers: [...(a.linked_ilo_numbers ?? [])],
        })),
      );
      setPhase(mode);
    } catch (e) {
      if (isQuotaError(e)) {
        setQuota(e.data);
      } else {
        setProposeError(
          e instanceof APIError && e.status === 429
            ? t("chat.rateLimited")
            : e instanceof APIError
              ? e.detail
              : t("flawfix.proposeFailed"),
        );
      }
      setPhase("idle");
    }
  }

  // Sequentielles Anwenden über die bestehenden CRUD-Endpunkte; Fehler
  // werden gesammelt und ehrlich angezeigt (kein stiller Teil-Apply).
  async function applyActions(actions: FixAction[]): Promise<string[]> {
    const errors: string[] = [];
    for (const a of actions) {
      try {
        switch (a.action) {
          case "update_ilo":
            if (!a.ilo_id) throw new Error("ilo_id fehlt");
            await api.updateILO(props.courseId, a.ilo_id, {
              ...(a.text != null && { text: a.text }),
              ...(a.action_verb != null && { action_verb: a.action_verb }),
              ...(a.bender_depth != null && { bender_depth: a.bender_depth }),
            });
            break;
          case "update_tla":
            if (!a.tla_id) throw new Error("tla_id fehlt");
            await api.updateTLA(props.courseId, a.tla_id, {
              ...(a.description != null && { description: a.description }),
              ...(a.type != null && { type: a.type }),
              ...(a.modality != null && { modality: a.modality }),
              ...(a.duration_minutes != null && { duration_minutes: a.duration_minutes }),
            });
            break;
          case "update_at":
            if (!a.at_id) throw new Error("at_id fehlt");
            await api.updateAT(props.courseId, a.at_id, {
              ...(a.description != null && { description: a.description }),
              ...(a.type != null && { type: a.type }),
              ...(a.format != null && { format: a.format }),
              ...(a.duration_minutes != null && { duration_minutes: a.duration_minutes }),
            });
            break;
          case "link_tla":
            if (!a.tla_id) throw new Error("tla_id fehlt");
            await api.updateTLA(props.courseId, a.tla_id, {
              linked_ilo_ids: a.linked_ilo_ids ?? [],
            });
            break;
          case "link_at":
            if (!a.at_id) throw new Error("at_id fehlt");
            await api.updateAT(props.courseId, a.at_id, {
              linked_ilo_ids: a.linked_ilo_ids ?? [],
            });
            break;
          case "create_tla":
            await api.createTLA(props.courseId, {
              type: a.type ?? undefined,
              description: a.description ?? undefined,
              ...(a.modality != null && { modality: a.modality }),
              ...(a.duration_minutes != null && { duration_minutes: a.duration_minutes }),
              linked_ilo_ids: a.linked_ilo_ids ?? [],
            });
            break;
          case "create_at":
            await api.createAT(props.courseId, {
              type: a.type ?? undefined,
              description: a.description ?? undefined,
              ...(a.format != null && { format: a.format }),
              ...(a.duration_minutes != null && { duration_minutes: a.duration_minutes }),
              linked_ilo_ids: a.linked_ilo_ids ?? [],
            });
            break;
        }
      } catch (e) {
        errors.push(
          `${actionLine(a)}: ${e instanceof APIError ? e.detail : String(e)}`,
        );
      }
    }
    return errors;
  }

  async function apply(mode: "preview" | "edit") {
    if (!fix) return;
    const actions = mode === "edit" ? editActions : fix.actions;
    setPhase("applying");
    const errors = await applyActions(actions);
    if (errors.length > 0) {
      setApplyErrors(errors);
      setPhase(mode);
      return;
    }
    setApplyErrors([]);
    setPhase("done");
    props.onApplied();
  }

  function updateEditAction(i: number, patch: Partial<FixAction>) {
    setEditActions((prev) =>
      prev.map((a, j) => (j === i ? { ...a, ...patch } : a)),
    );
  }

  // LZ-Chip im Edit-Modus: Nummer togglen, UUIDs synchron mitführen.
  function toggleEditLink(i: number, iloNumber: number) {
    setEditActions((prev) =>
      prev.map((a, j) => {
        if (j !== i) return a;
        const numbers = a.linked_ilo_numbers ?? [];
        const next = numbers.includes(iloNumber)
          ? numbers.filter((k) => k !== iloNumber)
          : [...numbers, iloNumber].sort((x, y) => x - y);
        return {
          ...a,
          linked_ilo_numbers: next,
          linked_ilo_ids: next
            .map((k) => props.iloIds[k - 1])
            .filter((id): id is string => Boolean(id)),
        };
      }),
    );
  }

  // Editierbare Felder je Aktions-Typ (Edit-Modus).
  function renderEditFields(a: FixAction, i: number) {
    const withLinks =
      a.action.startsWith("link_") || a.action.startsWith("create_");
    return (
      <div className="space-y-2">
        {a.action === "update_ilo" && (
          <>
            {a.text != null && (
              <textarea
                value={a.text}
                onChange={(e) => updateEditAction(i, { text: e.target.value })}
                aria-label={t("flawfix.edit.iloText")}
                rows={3}
                className="w-full bg-white border border-ca-surface-border rounded-md px-3 py-2 text-sm text-ca-surface-ink outline-none focus:border-ca-accent resize-y"
              />
            )}
            <div className="flex gap-2 flex-wrap">
              {a.action_verb != null && (
                <input
                  value={a.action_verb}
                  onChange={(e) => updateEditAction(i, { action_verb: e.target.value })}
                  aria-label={t("flawfix.edit.verb")}
                  className="flex-1 min-w-32 bg-white border border-ca-surface-border rounded-md px-3 py-2 text-sm text-ca-surface-ink outline-none focus:border-ca-accent"
                />
              )}
              {a.bender_depth != null && (
                <select
                  value={a.bender_depth}
                  onChange={(e) =>
                    updateEditAction(i, {
                      bender_depth: e.target.value as FixAction["bender_depth"],
                    })
                  }
                  aria-label={t("flawfix.edit.depth")}
                  className="bg-white border border-ca-surface-border rounded-md px-3 py-2 text-sm text-ca-surface-ink outline-none focus:border-ca-accent"
                >
                  {BENDER_DEPTHS.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              )}
            </div>
          </>
        )}
        {(a.action === "update_tla" ||
          a.action === "update_at" ||
          a.action === "create_tla" ||
          a.action === "create_at") && (
          <>
            <div className="flex gap-2 flex-wrap">
              {a.type != null && (
                <input
                  value={a.type}
                  onChange={(e) => updateEditAction(i, { type: e.target.value })}
                  aria-label={t("flawfix.edit.type")}
                  className="flex-1 min-w-32 bg-white border border-ca-surface-border rounded-md px-3 py-2 text-sm text-ca-surface-ink outline-none focus:border-ca-accent"
                />
              )}
              {a.duration_minutes != null && (
                <input
                  type="number"
                  value={a.duration_minutes}
                  onChange={(e) =>
                    updateEditAction(i, {
                      duration_minutes: Number(e.target.value) || 0,
                    })
                  }
                  aria-label={t("flawfix.edit.duration")}
                  className="w-28 bg-white border border-ca-surface-border rounded-md px-3 py-2 text-sm text-ca-surface-ink outline-none focus:border-ca-accent"
                />
              )}
            </div>
            {a.description != null && (
              <textarea
                value={a.description}
                onChange={(e) => updateEditAction(i, { description: e.target.value })}
                aria-label={t("flawfix.edit.description")}
                rows={3}
                className="w-full bg-white border border-ca-surface-border rounded-md px-3 py-2 text-sm text-ca-surface-ink outline-none focus:border-ca-accent resize-y"
              />
            )}
          </>
        )}
        {withLinks && (
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-ca-surface-muted mb-1">
              {t("flawfix.edit.linkedIlos")}
            </p>
            <div className="flex gap-1 flex-wrap">
              {props.iloIds.map((_, k) => {
                const active = (a.linked_ilo_numbers ?? []).includes(k + 1);
                return (
                  <button
                    key={k}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggleEditLink(i, k + 1)}
                    className={`px-3 py-1 rounded-md text-xs font-bold border transition ${
                      active
                        ? "bg-ca-accent text-ca-brand border-ca-accent"
                        : "bg-ca-card text-ca-surface-muted border-ca-surface-border hover:text-ca-surface-ink"
                    }`}
                  >
                    LZ {k + 1}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  const showCard = phase === "preview" || phase === "edit" || phase === "applying";
  const cardActions = phase === "edit" ? editActions : (fix?.actions ?? []);

  return (
    <div className="mt-5 pt-5 border-t border-ca-surface-border">
      {/* Aktions-Leiste (FlawActionBar) */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void propose("preview")}
          disabled={phase === "loading" || phase === "applying"}
          className="px-4 py-2 bg-ca-accent text-ca-brand text-xs font-black uppercase tracking-widest rounded-xl hover:bg-brand-gold-dark transition disabled:opacity-40"
        >
          {t("flawfix.apply")}
        </button>
        <button
          type="button"
          onClick={() => void propose("edit")}
          disabled={phase === "loading" || phase === "applying"}
          className="px-4 py-2 border border-ca-surface-border text-ca-surface-ink text-xs font-black uppercase tracking-widest rounded-xl hover:border-ca-accent transition disabled:opacity-40"
        >
          {t("flawfix.applyModified")}
        </button>
        <button
          type="button"
          onClick={props.onDiscuss}
          disabled={phase === "loading" || phase === "applying"}
          className="px-4 py-2 border border-ca-surface-border text-ca-surface-muted text-xs font-black uppercase tracking-widest rounded-xl hover:text-ca-surface-ink hover:border-ca-accent transition disabled:opacity-40"
        >
          {t("flawfix.discuss")}
        </button>
        <span className="self-center text-[11px]">
          <CreditCost feature="propose_fix" />
        </span>
      </div>

      {quota !== null && (
        <div className="mt-3">
          <QuotaExhaustedCard detail={quota} onDismiss={() => setQuota(null)} />
        </div>
      )}
      {phase === "loading" && (
        <p className="mt-3 text-xs text-ca-surface-muted">{t("flawfix.proposing")}</p>
      )}
      {proposeError && (
        <p className="mt-3 text-xs text-verdict-red">{proposeError}</p>
      )}

      {/* FixPreviewCard: Vorschau (view) oder Edit-Modus */}
      {showCard && fix && (
        <div className="mt-4 p-5 rounded-2xl border-2 border-ca-accent bg-ca-accent/5">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-ca-surface-ink mb-2">
            {phase === "edit" ? t("flawfix.editTitle") : t("flawfix.previewTitle")}
          </p>
          {fix.rationale && (
            <p className="text-sm text-ca-surface-ink mb-3">{fix.rationale}</p>
          )}
          {fix.course_newer_than_report && (
            <p className="text-xs font-bold text-verdict-orange bg-verdict-orange-bg border border-verdict-orange/60 rounded-lg px-3 py-2 mb-3">
              {t("flawfix.staleWarning")}
            </p>
          )}

          {cardActions.length === 0 ? (
            <p className="text-sm text-ca-surface-muted italic mb-3">
              {t("flawfix.noActions")}
            </p>
          ) : phase === "edit" ? (
            <div className="space-y-4 mb-3">
              {cardActions.map((a, i) => (
                <div key={i} className="p-3 bg-ca-card rounded-xl border border-ca-surface-border">
                  <p className="text-xs font-bold text-ca-surface-ink mb-2">{actionLine(a)}</p>
                  {renderEditFields(a, i)}
                </div>
              ))}
            </div>
          ) : (
            <ul className="text-sm text-ca-surface-ink list-disc pl-5 mb-3 space-y-1">
              {cardActions.map((a, i) => (
                <li key={i}>{actionLine(a)}</li>
              ))}
            </ul>
          )}

          {/* Geister-Regel: verworfene Teil-Aktionen werden ausgewiesen */}
          {fix.dropped_actions.length > 0 && (
            <div className="mb-3 p-3 rounded-xl bg-verdict-orange-bg border border-verdict-orange/60">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-800 mb-1">
                {t("flawfix.droppedTitle")}
              </p>
              <ul className="text-xs text-orange-900 space-y-1">
                {fix.dropped_actions.map((d, i) => (
                  <li key={i}>
                    {d.action} — {d.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {applyErrors.length > 0 && (
            <div className="mb-3 p-3 rounded-xl bg-verdict-red-bg border border-verdict-red/60">
              <p className="text-xs font-bold text-verdict-red mb-1">
                {t("flawfix.applyErrors")}
              </p>
              <ul className="text-xs text-verdict-red space-y-1">
                {applyErrors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex gap-2">
            {cardActions.length > 0 && (
              <button
                type="button"
                onClick={() => void apply(phase === "edit" ? "edit" : "preview")}
                disabled={phase === "applying"}
                className="px-4 py-2 bg-ca-accent text-ca-brand text-sm font-bold rounded-md hover:bg-brand-gold-dark transition disabled:opacity-40"
              >
                {phase === "applying" ? t("flawfix.applying") : t("flawfix.confirm")}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setPhase("idle");
                setFix(null);
                setApplyErrors([]);
              }}
              disabled={phase === "applying"}
              className="px-4 py-2 border border-ca-surface-border text-ca-surface-muted text-sm font-semibold rounded-md hover:text-ca-surface-ink transition disabled:opacity-40"
            >
              {t("flawfix.discard")}
            </button>
          </div>
        </div>
      )}

      {phase === "done" && (
        <p className="mt-3 text-sm font-black text-ca-surface-ink">
          {t("flawfix.applied")}
        </p>
      )}
    </div>
  );
}
